import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import appConfigApi from '../../services/AppConfigDeployer/AppConfigDeployerService';
import { formatConfigValue } from './processorPaymentMethodList';
import { ConfigRow, RawConfig, configToRows, rowsToConfig, rowKey, toAwsRegion } from './types';

export function useAppConfigEditor(stage: string, region: string, selectedUseCase: string, userAlias: string) {
    // Request counter to discard stale responses when selectedUseCase changes rapidly
    const loadConfigRequestId = useRef(0);

    const [originalRows, setOriginalRows] = useState<ConfigRow[]>([]);
    const [editedRows, setEditedRows] = useState<ConfigRow[]>([]);
    const [configDescription, setConfigDescription] = useState('');
    const [currentVersion, setCurrentVersion] = useState(0);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
    const [showWeblabModal, setShowWeblabModal] = useState(false);
    const [showCapModal, setShowCapModal] = useState(false);
    const [showDiff, setShowDiff] = useState(false);
    const [newProcessor, setNewProcessor] = useState('');
    const [newPaymentMethod, setNewPaymentMethod] = useState('');
    const [addEntryError, setAddEntryError] = useState<string | null>(null);
    const [newEntryKeys, setNewEntryKeys] = useState<Set<string>>(new Set());
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const loadConfig = useCallback(async () => {
        if (!selectedUseCase) return;
        const requestId = ++loadConfigRequestId.current;
        setSaveError(null);
        setSaveSuccess(null);
        setSelectedIndices(new Set());
        setNewEntryKeys(new Set());
        setAddEntryError(null);
        setLoadingConfig(true);
        try {
            const result = await appConfigApi.getConfig(stage, region, selectedUseCase);
            // Discard stale response if a newer request has been issued
            if (requestId !== loadConfigRequestId.current) return;
            const content = result?.content ?? '{}';
            const parsed: RawConfig = typeof content === 'string' ? JSON.parse(content) : content;
            const rows = configToRows(parsed);
            setOriginalRows(rows);
            setEditedRows(rows);
            setConfigDescription(parsed.description || '');
            setCurrentVersion(result?.versionNumber ?? 0);
        } catch (e: any) {
            if (requestId !== loadConfigRequestId.current) return;
            console.error('loadConfig error:', e);
            setOriginalRows([]);
            setEditedRows([]);
        } finally {
            if (requestId === loadConfigRequestId.current) {
                setLoadingConfig(false);
            }
        }
    }, [stage, region, selectedUseCase]);

    useEffect(() => {
        if (selectedUseCase) loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUseCase]);

    const filteredIndices = useMemo(() => {
        const term = searchTerm.toLowerCase();
        const newIndices: number[] = [];
        const existingIndices: number[] = [];
        editedRows.forEach((r, i) => {
            if (term && !r.processor.toLowerCase().includes(term) && !r.paymentMethod.toLowerCase().includes(term)) return;
            if (newEntryKeys.has(rowKey(r.processor, r.paymentMethod))) {
                newIndices.push(i);
            } else {
                existingIndices.push(i);
            }
        });
        return [...newIndices, ...existingIndices];
    }, [editedRows, searchTerm, newEntryKeys]);

    const hasChanges = useMemo(() => JSON.stringify(editedRows) !== JSON.stringify(originalRows), [editedRows, originalRows]);

    const incompleteEntries = useMemo(() => {
        const incomplete = new Set<string>();
        editedRows.forEach(r => {
            if (newEntryKeys.has(rowKey(r.processor, r.paymentMethod)) && (!r.weblab || !r.amountCap)) {
                incomplete.add(rowKey(r.processor, r.paymentMethod));
            }
        });
        return incomplete;
    }, [editedRows, newEntryKeys]);

    const toggleSelect = (idx: number) => {
        const next = new Set(selectedIndices);
        next.has(idx) ? next.delete(idx) : next.add(idx);
        setSelectedIndices(next);
    };

    const toggleSelectAll = () => {
        setSelectedIndices(selectedIndices.size === filteredIndices.length ? new Set() : new Set(filteredIndices));
    };

    const applyWeblab = (weblab: string) => {
        const next = [...editedRows];
        selectedIndices.forEach(i => { next[i] = { ...next[i], weblab }; });
        setEditedRows(next);
        setShowWeblabModal(false);
    };

    const applyCap = (cap: string) => {
        const next = [...editedRows];
        selectedIndices.forEach(i => { next[i] = { ...next[i], amountCap: cap }; });
        setEditedRows(next);
        setShowCapModal(false);
    };

    const deleteSelected = () => {
        const indicesToRemove = new Set(selectedIndices);
        const remaining = editedRows.filter((_, i) => !indicesToRemove.has(i));
        const updatedNewKeys = new Set(newEntryKeys);
        indicesToRemove.forEach(i => {
            const row = editedRows[i];
            if (row) updatedNewKeys.delete(rowKey(row.processor, row.paymentMethod));
        });
        setEditedRows(remaining);
        setNewEntryKeys(updatedNewKeys);
        setSelectedIndices(new Set());
    };

    const addEntry = () => {
        setAddEntryError(null);
        const processor = newProcessor.trim();
        const paymentMethod = newPaymentMethod.trim();

        if (!processor && !paymentMethod) { setAddEntryError('Processor and Payment Method are required'); return; }
        if (!processor) { setAddEntryError('Processor name is required'); return; }
        if (!paymentMethod) { setAddEntryError('Payment Method is required'); return; }

        const key = rowKey(processor, paymentMethod);
        if (editedRows.some(r => rowKey(r.processor, r.paymentMethod) === key)) {
            setAddEntryError(`${processor} / ${paymentMethod} combination already exists`);
            return;
        }

        const updatedRows = [...editedRows, { processor, paymentMethod, weblab: '', amountCap: '' }];
        setEditedRows(updatedRows);
        setNewEntryKeys(prev => new Set(prev).add(key));
        setSelectedIndices(prev => new Set(prev).add(updatedRows.length - 1));
        setNewProcessor('');
        setNewPaymentMethod('');
    };

    // Conflict popup state
    const [conflictError, setConflictError] = useState<string | null>(null);

    const handleSaveVersion = async () => {
        if (incompleteEntries.size > 0) {
            setSaveError('Please select Weblab and Amount Cap for all new entries before saving.');
            return;
        }
        setSaveLoading(true);
        setSaveError(null);
        setSaveSuccess(null);
        setConflictError(null);
        try {
            const configJson = JSON.stringify(rowsToConfig(editedRows, configDescription), null, 2);

            // Compute only the changed processor/paymentMethod combos
            const originalMap = new Map(originalRows.map(r => [rowKey(r.processor, r.paymentMethod), formatConfigValue(r.weblab, r.amountCap)]));
            const changes: Record<string, Record<string, Record<string, string>>> = {};
            editedRows.forEach(row => {
                const key = rowKey(row.processor, row.paymentMethod);
                const newVal = formatConfigValue(row.weblab, row.amountCap);
                if (originalMap.get(key) !== newVal || !originalMap.has(key)) {
                    if (!changes[row.processor]) changes[row.processor] = {};
                    changes[row.processor][row.paymentMethod] = { weblab: row.weblab, amountCap: row.amountCap };
                }
            });
            // Also detect deleted rows
            originalRows.forEach(row => {
                if (!editedRows.some(r => r.processor === row.processor && r.paymentMethod === row.paymentMethod)) {
                    if (!changes[row.processor]) changes[row.processor] = {};
                    changes[row.processor][row.paymentMethod] = { weblab: '__DELETED__', amountCap: '__DELETED__' };
                }
            });

            await appConfigApi.createChangeRequest(stage, region, {
                useCase: selectedUseCase,
                stage,
                region: toAwsRegion(region),
                requestor: userAlias || 'unknown',
                assignedReviewer: '',
                fromVersion: currentVersion,
                proposedConfig: configJson,
                changes,
            });
            setSaveSuccess('Version saved for approval. It will be deployed once approved.');
        } catch (e: any) {
            const msg = e?.message || 'Failed to save version';
            const isConflict = msg.includes('409') || msg.toLowerCase().includes('conflict') || msg.toLowerCase().includes('pending change request');
            if (isConflict) {
                setConflictError('A pending change request exists for one or more of the same processor/payment method combinations. Please approve or reject it first!');
            } else {
                setSaveError(msg);
            }
        } finally {
            setSaveLoading(false);
        }
    };

    const resetEditor = () => {
        setOriginalRows([]);
        setEditedRows([]);
        setNewEntryKeys(new Set());
    };

    return {
        originalRows, editedRows, configDescription, currentVersion, loadingConfig,
        selectedIndices, showWeblabModal, setShowWeblabModal, showCapModal, setShowCapModal,
        showDiff, setShowDiff, newProcessor, setNewProcessor, newPaymentMethod, setNewPaymentMethod,
        addEntryError, setAddEntryError, newEntryKeys, saveLoading, saveError, saveSuccess,
        searchTerm, setSearchTerm, filteredIndices, hasChanges, incompleteEntries,
        conflictError, setConflictError,
        loadConfig, toggleSelect, toggleSelectAll, applyWeblab, applyCap,
        deleteSelected, addEntry, handleSaveVersion, resetEditor,
    };
}
